import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Search } from "lucide-react";
import { http } from "../lib/api";
import { BookCover, Stamp } from "../components/Bits";

const TILTS = ["-rotate-2", "rotate-1", "rotate-2", "-rotate-1", "rotate-0"];

function ShelfBook({ b, i }) {
  const tags = [...(b.kit?.tribe_tags || []), ...b.moods].slice(0, 3);
  return (
    <Link to={`/b/${b.slug}?ref=discover`} className="group block break-inside-avoid mb-14 rise" style={{ animationDelay: `${Math.min(i, 8) * 60}ms` }} data-testid={`discover-book-${b.slug}`}>
      <BookCover src={b.cover_url} title={b.title} author={b.author?.name}
        className={`${TILTS[i % TILTS.length]} ${i % 3 === 1 ? "w-[82%]" : "w-[92%]"} mx-auto transition-transform duration-300 group-hover:-translate-y-2 group-hover:rotate-0`} />
      <div className="mt-6 px-1">
        <div className="flex items-center gap-2">
          <p className="label-mono">{b.genre || "Fiction"}</p>
          {b.is_sample && <span className="font-type text-[0.6rem] uppercase text-[#C84B31]">· Sample</span>}
        </div>
        <h3 className="font-display text-2xl leading-tight mt-1 group-hover:text-[#C84B31] transition-colors">{b.title}</h3>
        <p className="text-sm text-[#5C584E] mt-0.5">by {b.author?.name}</p>
        {b.kit?.hook && <p className="mt-3 italic text-[#5C584E] text-[0.95rem] leading-snug line-clamp-3">{b.kit.hook}</p>}
        <div className="flex flex-wrap gap-2 mt-4">{tags.map((t) => <Stamp key={t}>{t}</Stamp>)}</div>
      </div>
    </Link>
  );
}

function Filters({ facets, tag, setTag, genre, setGenre }) {
  return (
    <div className="space-y-5">
      <div>
        <p className="label-mono mb-3">Moods, tropes & themes</p>
        <div className="flex flex-wrap gap-2.5">
          {facets.tags.map((t) => (
            <Stamp key={t} active={tag === t} onClick={() => setTag(tag === t ? "" : t)} testid={`discover-tag-filter-${t.replace(/\s+/g, "-")}`}>{t}</Stamp>
          ))}
        </div>
      </div>
      <div>
        <p className="label-mono mb-3">Genre</p>
        <div className="flex flex-wrap gap-x-5 gap-y-2">
          {facets.genres.map((g) => (
            <button key={g} onClick={() => setGenre(genre === g ? "" : g)} data-testid={`discover-genre-filter-${g.replace(/\s+/g, "-")}`}
              className={`font-display text-lg border-b transition-colors ${genre === g ? "border-[#C84B31] text-[#C84B31] italic" : "border-transparent hover:text-[#C84B31]"}`}>{g}</button>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function Discover() {
  const [facets, setFacets] = useState({ tags: [], genres: [] });
  const [books, setBooks] = useState(null);
  const [tag, setTag] = useState("");
  const [genre, setGenre] = useState("");
  const [fans, setFans] = useState("");
  const [q, setQ] = useState("");

  useEffect(() => {
    http.get("/public/facets").then(({ data }) => setFacets(data));
  }, []);

  useEffect(() => {
    const t = setTimeout(() => {
      http.get("/public/discover", { params: { tag, genre, fans, q } }).then(({ data }) => setBooks(data));
    }, 250);
    return () => clearTimeout(t);
  }, [tag, genre, fans, q]);

  const clear = () => { setTag(""); setGenre(""); setFans(""); setQ(""); };

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-14 pb-10" data-testid="discover-page">
      <div className="grid lg:grid-cols-12 gap-10 items-end">
        <div className="lg:col-span-6">
          <p className="label-mono">The indie table</p>
          <h1 className="font-display text-5xl sm:text-6xl mt-3 tracking-tight">Find your <span className="italic scribble">tribe.</span></h1>
          <p className="mt-5 text-[#5C584E] max-w-md leading-relaxed">Browse independent books the way you'd browse a good bookshop: by feeling first.</p>
        </div>
        <div className="lg:col-span-6 grid sm:grid-cols-2 gap-3">
          <label className="relative">
            <Search size={15} className="absolute left-3 top-3.5 text-[#8E8879]" />
            <input className="input-paper !pl-9" placeholder="Search titles or hooks" value={q} onChange={(e) => setQ(e.target.value)} data-testid="discover-search-input" />
          </label>
          <input className="input-paper font-display italic" placeholder="For fans of… (e.g. Holly Black)" value={fans} onChange={(e) => setFans(e.target.value)} data-testid="discover-fans-input" />
        </div>
      </div>
      <div className="mt-12 border-y border-[#E5DEC9] py-8">
        <Filters facets={facets} tag={tag} setTag={setTag} genre={genre} setGenre={setGenre} />
        {(tag || genre || fans || q) && <button className="btn-ghost mt-5 !px-0" onClick={clear} data-testid="discover-clear-filters">Clear filters ×</button>}
      </div>
      <div className="mt-14">
        {books === null ? <p className="label-mono caret">Arranging the table</p> : books.length === 0 ? (
          <div className="py-20 text-center" data-testid="discover-empty">
            <p className="font-display text-3xl italic">Nothing on this table yet.</p>
            <p className="text-[#5C584E] mt-2">Try another mood, or clear the filters.</p>
          </div>
        ) : (
          <div className="columns-2 md:columns-3 lg:columns-4 gap-8" data-testid="discover-results">
            {books.map((b, i) => <ShelfBook key={b.id} b={b} i={i} />)}
          </div>
        )}
      </div>
    </div>
  );
}
